import React, { useMemo } from "react";
import { Check, Minus } from "lucide-react";
import { formatIDR } from "@/lib/api";

const normalizeFeatures = (features = []) => {
  const seen = new Set();
  return features.filter((feature) => {
    if (!feature?.key || seen.has(feature.key)) return false;
    seen.add(feature.key);
    return true;
  });
};

export default function PlanFeatureMatrix({ plans = [], features = [], showStatus = false, compact = false }) {
  const matrixFeatures = useMemo(() => normalizeFeatures(features), [features]);
  const featureGroups = useMemo(() => {
    const groups = [];
    const byCategory = new Map();
    matrixFeatures.forEach((feature) => {
      const category = feature.category || "Fitur Lainnya";
      if (!byCategory.has(category)) {
        const group = { name: category, features: [] };
        byCategory.set(category, group);
        groups.push(group);
      }
      byCategory.get(category).features.push(feature);
    });
    return groups;
  }, [matrixFeatures]);

  if (!plans.length) {
    return (
      <div className="border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
        Belum ada paket untuk dibandingkan.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-border bg-white" data-testid="plan-feature-matrix">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="bg-zinc-950 text-white">
            <th className="sticky left-0 z-20 min-w-[230px] border-r border-zinc-700 bg-zinc-950 px-4 py-4 text-left font-semibold">
              Perbandingan
            </th>
            {plans.map((plan) => (
              <th key={plan.id || plan.name} className="min-w-[175px] border-r border-zinc-700 px-4 py-4 text-center last:border-r-0">
                <div className="font-display text-lg font-black tracking-tight">{plan.name}</div>
                {showStatus && (
                  <div className={`mt-1 text-[10px] uppercase tracking-widest ${plan.active ? "text-emerald-300" : "text-zinc-400"}`}>
                    {plan.active ? "Aktif" : "Nonaktif"}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border bg-zinc-50">
            <th className="sticky left-0 z-10 border-r border-border bg-zinc-50 px-4 py-3 text-left font-semibold">Harga bulanan</th>
            {plans.map((plan) => (
              <td key={plan.id || plan.name} className="border-r border-border px-4 py-3 text-center font-semibold tabular-nums last:border-r-0">
                {formatIDR(plan.price_monthly)}
              </td>
            ))}
          </tr>
          {!compact && (
            <tr className="border-b border-border bg-white">
              <th className="sticky left-0 z-10 border-r border-border bg-white px-4 py-3 text-left font-semibold">Harga tahunan</th>
              {plans.map((plan) => (
                <td key={plan.id || plan.name} className="border-r border-border px-4 py-3 text-center tabular-nums last:border-r-0">
                  {formatIDR(plan.price_yearly)}
                </td>
              ))}
            </tr>
          )}
          <tr className="border-b border-border bg-zinc-50">
            <th className="sticky left-0 z-10 border-r border-border bg-zinc-50 px-4 py-3 text-left font-semibold">Maksimum outlet aktif</th>
            {plans.map((plan) => (
              <td key={plan.id || plan.name} className="border-r border-border px-4 py-3 text-center font-semibold tabular-nums last:border-r-0">
                {plan.max_outlets}
              </td>
            ))}
          </tr>
          <tr className="border-b border-border bg-white">
            <th className="sticky left-0 z-10 border-r border-border bg-white px-4 py-3 text-left font-semibold">Maksimum pengguna aktif</th>
            {plans.map((plan) => (
              <td key={plan.id || plan.name} className="border-r border-border px-4 py-3 text-center font-semibold tabular-nums last:border-r-0">
                {plan.max_users}
              </td>
            ))}
          </tr>

          {featureGroups.map((group) => (
            <React.Fragment key={group.name}>
              <tr className="border-y border-border bg-blue-50">
                <th colSpan={plans.length + 1} className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-widest text-[#002FA7]">
                  {group.name}
                </th>
              </tr>
              {group.features.map((feature, index) => (
                <tr key={feature.key} className={`border-b border-border ${index % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}`}>
                  <th className={`sticky left-0 z-10 border-r border-border px-4 py-3 text-left font-medium ${index % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}>
                    <div>{feature.label}</div>
                    {!compact && feature.description && <div className="mt-0.5 max-w-[320px] text-[11px] font-normal leading-relaxed text-zinc-500">{feature.description}</div>}
                  </th>
                  {plans.map((plan) => {
                    const included = (plan.feature_keys || []).includes(feature.key);
                    return (
                      <td key={plan.id || plan.name} className="border-r border-border px-4 py-3 text-center last:border-r-0">
                        {included ? (
                          <Check className="mx-auto h-5 w-5 text-emerald-600" aria-label="Tersedia" />
                        ) : (
                          <Minus className="mx-auto h-4 w-4 text-zinc-300" aria-label="Tidak tersedia" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
